import React from 'react';
import styled from 'styled-components';
import { useTranslation } from '../translations';

/**
 * Pola kwot (U.25): zaliczka (`AmountField`) i rabat z jednostką (`DiscountField`).
 *
 * Oba są KONTROLOWANE SUROWYM TEKSTEM: `value` to dokładnie to, co wpisał użytkownik, a liczbę
 * liczy strona przez `utils/amountField.js`. Dlatego `type="text"` z `inputMode="decimal"`,
 * a nie `type="number"`: to drugie przy polskim przecinku oddaje pustą wartość, pozwala zmieniać
 * kwotę kółkiem myszy i nie daje zostawić pola pustego. Komponenty niczego nie przycinają w trakcie
 * pisania; błąd podaje strona w `invalid`, a komunikat renderuje sama pod polem w `AmountNote`.
 *
 * Używane w Nowej rezerwacji, w edycji rezerwacji i w ofercie (U.26), stąd miejsce w `src/ui/`.
 */

const Box = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 44px;
  box-sizing: border-box;

  /* Na dotyku przełącznik jednostki ma mieć pełne 44 px (patrz Segments), więc pole rośnie. */
  @media (max-width: 768px), (hover: none) and (pointer: coarse) {
    height: 48px;
  }
  border: 2px solid var(--input-border);
  border-radius: var(--r-md);
  background: var(--input-bg);
  transition: border-color var(--dur) var(--ease), box-shadow var(--dur) var(--ease);

  &:focus-within {
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  &[data-invalid='true'] {
    border-color: var(--danger);
  }
`;

const Field = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0 var(--sp-3);
  font-family: inherit;
  font-size: var(--fs-md);
  font-weight: 500;
  text-align: right;
  color: var(--text);
  outline: none;

  &::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }
  &:disabled {
    color: var(--text-muted);
  }
`;

const Suffix = styled.span`
  display: inline-flex;
  align-items: center;
  padding-right: var(--sp-3);
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--text-muted);
`;

const Segments = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-left: 1px solid var(--input-border);

  @media (max-width: 768px), (hover: none) and (pointer: coarse) {
    padding: 0;
  }
`;

const Segment = styled.button.attrs({ type: 'button' })`
  min-width: 40px;
  height: 100%;
  padding: 0 var(--sp-2);
  border: none;
  border-radius: calc(var(--r-md) - 4px);
  font-family: inherit;
  font-size: var(--fs-sm);
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p['aria-pressed'] ? 'var(--primary)' : 'transparent')};
  color: ${(p) => (p['aria-pressed'] ? 'var(--primary-contrast)' : 'var(--text-muted)')};
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);

  &:hover {
    color: ${(p) => (p['aria-pressed'] ? 'var(--primary-contrast)' : 'var(--text)')};
  }
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

// Miejsce pod polem na komunikat („Po rabacie: X zł" albo błąd limitu). Komunikat jest w przepływie,
// nie absolutny, bo przy wąskim ekranie dłuższy tekst nachodziłby na pole poniżej.
export const AmountNote = styled.div`
  min-height: 1.2em;
  margin-top: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-muted);

  &[data-error='true'] {
    color: var(--danger);
  }
`;

export function AmountField({
  id,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  invalid = false,
  describedBy,
  dataCy,
}) {
  const { t } = useTranslation();
  return (
    <Box data-invalid={invalid}>
      <Field
        id={id}
        name={name}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        data-cy={dataCy}
      />
      <Suffix aria-hidden="true">{t('amountField.currency')}</Suffix>
    </Box>
  );
}

// `name`, `ariaLabel`, `unitGroupLabel` i `disabled` (12.10.2026): to samo pole służy też opłacie
// za anulowanie w oknie anulowania rezerwacji (kwota albo procent wpłaty), gdzie podpis „Jednostka
// rabatu” byłby nieprawdą, a w trakcie zwrotu pole musi dać się zablokować. Domyślne wartości
// zostawiają rabat bez zmian.
export function DiscountField({
  id,
  name = 'discount',
  value,
  unit,
  onChange,
  onUnitChange,
  onBlur,
  placeholder,
  invalid = false,
  disabled = false,
  ariaLabel,
  unitGroupLabel,
  describedBy,
  dataCy,
}) {
  const { t } = useTranslation();
  const units = [
    { value: '%', label: '%' },
    { value: 'PLN', label: t('amountField.currency') },
  ];
  return (
    <Box data-invalid={invalid}>
      <Field
        id={id}
        name={name}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        data-cy={dataCy}
      />
      <Segments role="group" aria-label={unitGroupLabel || t('amountField.unitGroup')}>
        {units.map((u) => (
          <Segment
            key={u.value}
            aria-pressed={unit === u.value}
            disabled={disabled}
            onClick={() => onUnitChange(u.value)}
            data-cy={dataCy ? `${dataCy}-unit-${u.value}` : undefined}
          >
            {u.label}
          </Segment>
        ))}
      </Segments>
    </Box>
  );
}
